import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Plus, Upload, Download, Edit, Trash2, X, 
  Check, AlertCircle, Package,
  Euro, Percent, User
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL;

export default function ProductosLicitacion() {
  const { licitacionId } = useParams();
  const navigate = useNavigate();
  
  // Estados principales
  const [licitacion, setLicitacion] = useState(null);
  const [bloques, setBloques] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  
  // Estados para modales
  const [showNewBloqueModal, setShowNewBloqueModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [selectedBloque, setSelectedBloque] = useState(null);
  
  // Estados para formularios
  const [newBloqueData, setNewBloqueData] = useState({ nombre: '' });
  const [uploadFile, setUploadFile] = useState(null);
  const [addProductData, setAddProductData] = useState({
    proveedor_id: '',
    nuevo_proveedor: '',
    tipo_proveedor: 'existente'
  });
  
  // Estados para edición inline
  const [editingProduct, setEditingProduct] = useState(null);
  const [editData, setEditData] = useState({});

  // Estado para permisos del usuario
  const [userPermissions, setUserPermissions] = useState(null);

  // Obtener permisos del usuario del token
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUserPermissions({
          permisos: payload.permisos || 1,
          proveedor_asociado: payload.proveedor_asociado || null,
          username: payload.username || 'Usuario',
          id: payload.id || null
        });
      } catch (error) {
        console.error('Error al decodificar token:', error);
        setUserPermissions({ permisos: 1, proveedor_asociado: null, username: 'Admin', id: null });
      }
    } else {
      setUserPermissions({ permisos: 1, proveedor_asociado: null, username: 'Admin', id: null });
    }
  }, []);

  // Cargar datos iniciales
  useEffect(() => {
    if (licitacionId && userPermissions) {
      loadLicitacion();
      loadBloques();
      // Solo cargar proveedores si es admin
      if (userPermissions.permisos === 1) {
        loadProveedores();
      }
    }
  }, [licitacionId, userPermissions]);

  // Cargar información de la licitación
  const loadLicitacion = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/licitaciones/${licitacionId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setLicitacion(data.licitacion);
      } else {
        showMessage('Error al cargar la licitación', 'danger');
        navigate('/licitaciones');
      }
    } catch (error) {
      console.error('Error al cargar licitación:', error);
      showMessage('Error al cargar la licitación', 'danger');
    }
  };

  // Cargar bloques y productos
  const loadBloques = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/licitaciones/${licitacionId}/bloques`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setBloques(data.bloques || []);
      }
    } catch (error) {
      console.error('Error al cargar bloques:', error);
      showMessage('Error al cargar los productos', 'danger');
    } finally {
      setLoading(false);
    }
  };

  // Cargar proveedores (solo para admin)
  const loadProveedores = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/proveedores/search`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setProveedores(data.proveedores || []);
      }
    } catch (error) {
      console.error('Error al cargar proveedores:', error);
    }
  };

  // Crear nuevo bloque (solo admin)
  const handleCreateBloque = async (e) => {
    e.preventDefault();
    
    // Verificar permisos
    if (userPermissions?.permisos !== 1) {
      showMessage('No tiene permisos para crear bloques', 'danger');
      return;
    }
    
    if (!newBloqueData.nombre.trim()) {
      showMessage('El nombre del bloque es obligatorio', 'danger');
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/api/licitaciones/${licitacionId}/bloques`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newBloqueData)
      });

      if (response.ok) {
        showMessage('Bloque creado correctamente', 'success');
        setShowNewBloqueModal(false);
        setNewBloqueData({ nombre: '' });
        loadBloques();
      } else {
        const errorData = await response.json();
        showMessage(errorData.message || 'Error al crear el bloque', 'danger');
      }
    } catch (error) {
      console.error('Error al crear bloque:', error);
      showMessage('Error al crear el bloque', 'danger');
    } finally {
      setSaving(false);
    }
  };

  // Subir archivo Excel (solo admin)
  const handleUploadExcel = async (e) => {
    e.preventDefault();
    
    // Verificar permisos
    if (userPermissions?.permisos !== 1) {
      showMessage('No tiene permisos para subir archivos Excel', 'danger');
      return;
    }
    
    if (!uploadFile) {
      showMessage('Debe seleccionar un archivo Excel', 'danger');
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('excel_file', uploadFile);
      
      const response = await fetch(`${API_URL}/api/licitaciones/${licitacionId}/upload-excel`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();

      if (response.ok) {
        showMessage(
          `Excel procesado correctamente. ${data.estadisticas.bloques_creados} bloques creados, ${data.estadisticas.productos_creados} productos nuevos, ${data.estadisticas.productos_actualizados} productos actualizados.`, 
          'success'
        );
        setShowUploadModal(false);
        setUploadFile(null);
        loadBloques();
      } else {
        showMessage(data.message || 'Error al procesar el Excel', 'danger');
      }
    } catch (error) {
      console.error('Error al subir Excel:', error);
      showMessage('Error al procesar el archivo Excel', 'danger');
    } finally {
      setSaving(false);
    }
  };

  // Añadir producto a bloque (solo admin)
  const handleAddProduct = async (e) => {
    e.preventDefault();
    
    // Verificar permisos
    if (userPermissions?.permisos !== 1) {
      showMessage('No tiene permisos para añadir productos', 'danger');
      return;
    }
    
    if (addProductData.tipo_proveedor === 'existente' && !addProductData.proveedor_id) {
      showMessage('Debe seleccionar un proveedor', 'danger');
      return;
    }
    
    if (addProductData.tipo_proveedor === 'nuevo' && !addProductData.nuevo_proveedor.trim()) {
      showMessage('Debe ingresar el nombre del proveedor', 'danger');
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      const productData = {
        bloque_id: selectedBloque.id,
        proveedor_id: addProductData.tipo_proveedor === 'existente' ? addProductData.proveedor_id : null,
        nombre_proveedor: addProductData.tipo_proveedor === 'nuevo' ? addProductData.nuevo_proveedor : null
      };
      
      const response = await fetch(`${API_URL}/api/licitaciones/${licitacionId}/productos`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(productData)
      });

      if (response.ok) {
        showMessage('Producto añadido correctamente', 'success');
        setShowAddProductModal(false);
        setAddProductData({ proveedor_id: '', nuevo_proveedor: '', tipo_proveedor: 'existente' });
        loadBloques();
      } else {
        const errorData = await response.json();
        showMessage(errorData.message || 'Error al añadir el producto', 'danger');
      }
    } catch (error) {
      console.error('Error al añadir producto:', error);
      showMessage('Error al añadir el producto', 'danger');
    } finally {
      setSaving(false);
    }
  };

  // Eliminar bloque (solo admin)
  const handleDeleteBloque = async (bloqueId) => {
    // Verificar permisos
    if (userPermissions?.permisos !== 1) {
      showMessage('No tiene permisos para eliminar bloques', 'danger');
      return;
    }

    if (!window.confirm('¿Está seguro de eliminar este bloque y todos sus productos?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/licitaciones/${licitacionId}/bloques/${bloqueId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        showMessage('Bloque eliminado correctamente', 'success');
        loadBloques();
      } else {
        const errorData = await response.json();
        showMessage(errorData.message || 'Error al eliminar el bloque', 'danger');
      }
    } catch (error) {
      console.error('Error al eliminar bloque:', error);
      showMessage('Error al eliminar el bloque', 'danger');
    }
  };

  // Verificar si el usuario puede editar un producto específico
  const canEditProduct = (producto) => {
    if (!userPermissions) return false;
    
    // Admin puede editar cualquier producto
    if (userPermissions.permisos === 1) return true;
    
    // Proveedor solo puede editar sus propios productos y solo si tiene proveedor_id (no productos con solo nombre)
    if (userPermissions.permisos === 4) {
      return producto.proveedor_id === userPermissions.proveedor_asociado && producto.proveedor_id !== null;
    }
    
    return false;
  };

  // Iniciar edición de producto
  const startEditProduct = (producto) => {
    if (!canEditProduct(producto)) {
      showMessage('No puede editar un producto que no le pertenece', 'danger');
      return;
    }

    setEditingProduct(producto.id);
    setEditData({
      unidad_basica: producto.unidad_basica || '',
      ref_proveedor: producto.ref_proveedor || '',
      precio_unitario: producto.precio_unitario || 0,
      iva: producto.iva || 21
    });
  };

  // Guardar edición de producto
  const saveEditProduct = async (productoId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/licitaciones/${licitacionId}/productos/${productoId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editData)
      });

      if (response.ok) {
        showMessage('Producto actualizado correctamente', 'success');
        setEditingProduct(null);
        setEditData({});
        loadBloques();
      } else {
        const errorData = await response.json();
        showMessage(errorData.message || 'Error al actualizar el producto', 'danger');
      }
    } catch (error) {
      console.error('Error al actualizar producto:', error);
      showMessage('Error al actualizar el producto', 'danger');
    }
  };

  // Cancelar edición
  const cancelEdit = () => {
    setEditingProduct(null);
    setEditData({});
  };

  // Eliminar producto (solo admin)
  const handleDeleteProduct = async (productoId) => {
    // Verificar permisos
    if (userPermissions?.permisos !== 1) {
      showMessage('No tiene permisos para eliminar productos', 'danger');
      return;
    }

    if (!window.confirm('¿Está seguro de eliminar este producto?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/licitaciones/${licitacionId}/productos/${productoId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        showMessage('Producto eliminado correctamente', 'success');
        loadBloques();
      } else {
        const errorData = await response.json();
        showMessage(errorData.message || 'Error al eliminar el producto', 'danger');
      }
    } catch (error) {
      console.error('Error al eliminar producto:', error);
      showMessage('Error al eliminar el producto', 'danger');
    }
  };

  // Descargar Excel - Disponible para todos
  const handleDownloadExcel = async () => {
    try {
      setDownloading(true);
      const token = localStorage.getItem('token');

      const response = await fetch(`${API_URL}/api/licitaciones/${licitacionId}/export-excel`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        // Obtener el nombre del archivo del header Content-Disposition o usar uno por defecto
        const contentDisposition = response.headers.get('Content-Disposition');
        let fileName = `Licitacion_${licitacion?.titulo || 'productos'}_${new Date().toISOString().split('T')[0]}.xlsx`;
        
        if (contentDisposition) {
          const fileNameMatch = contentDisposition.match(/filename="(.+)"/);
          if (fileNameMatch) {
            fileName = fileNameMatch[1];
          }
        }

        // Crear blob y descargar
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        showMessage('Excel descargado correctamente', 'success');
      } else {
        const errorData = await response.json();
        showMessage(errorData.message || 'Error al descargar el Excel', 'danger');
      }
    } catch (error) {
      console.error('Error al descargar Excel:', error);
      showMessage('Error al descargar el Excel', 'danger');
    } finally {
      setDownloading(false);
    }
  };

  // Utilidades
  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 8080);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount || 0);
  };

  const calculateTotal = (cantidad, precio, iva) => {
    const cant = parseFloat(cantidad) || 0;
    const prec = parseFloat(precio) || 0;
    const ivaRate = parseFloat(iva) || 0;
    return cant * prec * (1 + ivaRate / 100);
  };

  const getUserRoleBadge = () => {
    if (!userPermissions) return null;
    
    switch (userPermissions.permisos) {
      case 1:
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            <User className="w-3 h-3 mr-1" />
            Administrador
          </span>
        );
      case 4:
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
            <User className="w-3 h-3 mr-1" />
            Proveedor
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            <User className="w-3 h-3 mr-1" />
            Usuario
          </span>
        );
    }
  };

  if (loading && !licitacion) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 border-2 border-green-500 border-t-transparent rounded-full animate-spin"></div>
          <span className="text-gray-600">Cargando licitación...</span>
        </div>
      </div>
    );
  }

  if (!userPermissions) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="flex items-center space-x-2">
          <div className="w-6 h-6 border-2 border-green-500 border-t-transparent rounded-full animate-spin"></div>
          <span className="text-gray-600">Cargando permisos...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="shadow-lg border-b-2 bg-white" style={{borderBottomColor: '#4DD5AF'}}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/licitaciones')}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-lg" style={{backgroundColor: '#4DD5AF'}}>
                <Package className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-800">
                  Productos - {licitacion?.titulo || 'Cargando...'}
                </h1>
                <p className="text-sm font-medium" style={{color: '#4DD5AF'}}>
                  {licitacion?.centro || ''}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {getUserRoleBadge()}
              <span className="text-sm text-gray-600">
                {userPermissions.username}
              </span>
              {licitacion?.estado === 1 && (
                <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                  Activa
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Info Bar */}
      <div className="text-white" style={{background: `linear-gradient(to right, #4DD5AF, #34d399, #10b981)`}}>
        <div className="max-w-7xl mx-auto px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Package className="w-4 h-4" />
              <span className="font-bold">
                {userPermissions.permisos === 4 ? 'Mis Productos en Licitación' : 'Gestión de Productos'}
              </span>
            </div>
            <div className="text-sm">
              {bloques.length} bloque{bloques.length !== 1 ? 's' : ''} configurado{bloques.length !== 1 ? 's' : ''}
            </div>
          </div>
        </div>
      </div>

      {/* Mensaje de estado */}
      {message.text && (
        <div className="max-w-7xl mx-auto px-6 pt-4">
          <div className={`p-3 rounded-lg flex items-center shadow-lg border text-sm ${
            message.type === 'success' ? 'border-emerald-200' : 'border-red-200'
          }`}
          style={{
            background: message.type === 'success' ? 'linear-gradient(135deg, #ecfdf5, #d1fae5)' : 'linear-gradient(135deg, #fef2f2, #fecaca)',
            color: message.type === 'success' ? '#065f46' : '#991b1b'
          }}>
            {message.type === 'success' ? (
              <Check className="w-4 h-4 mr-2 flex-shrink-0" />
            ) : (
              <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
            )}
            <span className="font-medium flex-1">{message.text}</span>
            <button 
              onClick={() => setMessage({ text: '', type: '' })}
              className="ml-auto p-1 hover:bg-white hover:bg-opacity-30 rounded"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        </div>
      )}

      {/* Barra de herramientas */}
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="bg-white rounded-xl shadow-lg border-2 p-4" style={{borderColor: '#4DD5AF'}}>
          <div className="flex flex-wrap gap-3">
            {/* Botones solo para admin y licitaciones activas */}
            {licitacion?.estado === 1 && userPermissions.permisos === 1 && (
              <>
                <button
                  onClick={() => setShowNewBloqueModal(true)}
                  className="px-4 py-2 text-white rounded-lg hover:opacity-90 flex items-center gap-2"
                  style={{backgroundColor: '#4DD5AF'}}
                  disabled={saving}
                >
                  <Plus className="w-4 h-4" />
                  Nuevo Bloque
                </button>
                
                <button
                  onClick={() => setShowUploadModal(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                  disabled={saving}
                >
                  <Upload className="w-4 h-4" />
                  Cargar Excel
                </button>
              </>
            )}
            
            {/* Botón de descarga disponible para todos si hay productos */}
            {bloques.length > 0 && (
              <button
                onClick={handleDownloadExcel}
                disabled={downloading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              >
                {downloading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Descargando...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    Descargar Excel
                  </>
                )}
              </button>
            )}

          </div>
        </div>
      </div>

      {/* Contenido principal */}
      <div className="max-w-7xl mx-auto px-6 pb-6">
        {loading ? (
          <div className="text-center py-8">
            <div className="flex items-center justify-center space-x-2">
              <div className="w-6 h-6 border-2 border-green-500 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-gray-600">Cargando productos...</span>
            </div>
          </div>
        ) : bloques.length === 0 ? (
          <div className="bg-white rounded-xl shadow-lg border-2 p-12 text-center" style={{borderColor: '#4DD5AF'}}>
            <Package className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {userPermissions.permisos === 4 ? 'No hay productos asignados' : 'No hay productos configurados'}
            </h3>
            <p className="text-gray-600 mb-6">
              {userPermissions.permisos === 4 
                ? 'No tiene productos asignados en esta licitación'
                : 'Comience creando un bloque de productos para esta licitación'
              }
            </p>
            {licitacion?.estado === 1 && userPermissions.permisos === 1 && (
              <button
                onClick={() => setShowNewBloqueModal(true)}
                className="px-6 py-3 text-white rounded-lg hover:opacity-90 flex items-center gap-2 mx-auto"
                style={{backgroundColor: '#4DD5AF'}}
              >
                <Plus className="w-5 h-5" />
                Crear Primer Bloque
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            {bloques.map((bloque) => (
              <div key={bloque.id} className="bg-white rounded-xl shadow-lg border-2" style={{borderColor: '#4DD5AF'}}>
                {/* Header del bloque */}
                <div className="p-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{backgroundColor: '#4DD5AF'}}>
                        <Package className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{bloque.nombre}</h3>
                        <p className="text-sm text-gray-600">
                          {bloque.productos?.length || 0} producto{(bloque.productos?.length || 0) !== 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>
                    
                    {/* Botones solo para admin y licitaciones activas */}
                    {licitacion?.estado === 1 && userPermissions.permisos === 1 && (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setSelectedBloque(bloque);
                            setShowAddProductModal(true);
                          }}
                          className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 flex items-center gap-1"
                        >
                          <Plus className="w-3 h-3" />
                          Añadir
                        </button>
                        
                        <button
                          onClick={() => handleDeleteBloque(bloque.id)}
                          className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700 flex items-center gap-1"
                        >
                          <Trash2 className="w-3 h-3" />
                          Eliminar
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Tabla de productos */}
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        {userPermissions.permisos === 1 && (
                          <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">ID</th>
                        )}
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Proveedor</th>
                        <th className="px-4 py-3 text-center text-sm font-medium text-gray-700">Ud. Venta</th>
                        <th className="px-4 py-3 text-center text-sm font-medium text-gray-700">Ref. Proveedor</th>
                        <th className="px-4 py-3 text-center text-sm font-medium text-gray-700">Precio Unit.</th>
                        <th className="px-4 py-3 text-center text-sm font-medium text-gray-700">IVA (%)</th>
                        <th className="px-4 py-3 text-center text-sm font-medium text-gray-700">Total</th>
                        {licitacion?.estado === 1 && (
                          <th className="px-4 py-3 text-center text-sm font-medium text-gray-700">Acciones</th>
                        )}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {!bloque.productos || bloque.productos.length === 0 ? (
                        <tr>
                          <td colSpan={userPermissions.permisos === 1 ? (licitacion?.estado === 1 ? "8" : "7") : (licitacion?.estado === 1 ? "7" : "6")} className="px-4 py-8 text-center text-gray-500">
                            <Package className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                            <p>No hay productos en este bloque</p>
                          </td>
                        </tr>
                      ) : (
                        bloque.productos.map((producto) => (
                          <tr key={producto.id} className="hover:bg-gray-50">
                            {userPermissions.permisos === 1 && (
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {producto.id}
                              </td>
                            )}
                            
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <User className="w-4 h-4 text-gray-400" />
                                <span className={producto.proveedor_id ? 'font-medium' : 'font-medium italic text-gray-600'}>
                                  {producto.proveedor_nombre}
                                  {!producto.proveedor_id && (
                                    <span className="text-xs text-gray-500 ml-1">(No registrado)</span>
                                  )}
                                </span>
                              </div>
                            </td>
                            
                            <td className="px-4 py-3 text-center">
                              {editingProduct === producto.id ? (
                                <input
                                  type="text"
                                  className="w-20 px-2 py-1 border rounded text-center"
                                  value={editData.unidad_basica}
                                  onChange={(e) => setEditData(prev => ({ ...prev, unidad_basica: e.target.value }))}
                                />
                              ) : (
                                <span className="text-sm">{producto.unidad_basica || '-'}</span>
                              )}
                            </td>
                            
                            <td className="px-4 py-3 text-center">
                              {editingProduct === producto.id ? (
                                <input
                                  type="text"
                                  className="w-24 px-2 py-1 border rounded text-center"
                                  value={editData.ref_proveedor}
                                  onChange={(e) => setEditData(prev => ({ ...prev, ref_proveedor: e.target.value }))}
                                />
                              ) : (
                                <span className="text-sm">{producto.ref_proveedor || '-'}</span>
                              )}
                            </td>
                            
                            <td className="px-4 py-3 text-center">
                              {editingProduct === producto.id ? (
                                <div className="flex items-center justify-center gap-1">
                                  <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    className="w-20 px-2 py-1 border rounded text-center"
                                    value={editData.precio_unitario}
                                    onChange={(e) => setEditData(prev => ({ ...prev, precio_unitario: e.target.value }))}
                                  />
                                  <Euro className="w-3 h-3 text-gray-400" />
                                </div>
                              ) : (
                                <span className="text-sm font-medium">{formatCurrency(producto.precio_unitario)}</span>
                              )}
                            </td>
                            
                            <td className="px-4 py-3 text-center">
                              {editingProduct === producto.id ? (
                                <div className="flex items-center justify-center gap-1">
                                  <input
                                    type="number"
                                    min="0"
                                    max="100"
                                    className="w-16 px-2 py-1 border rounded text-center"
                                    value={editData.iva}
                                    onChange={(e) => setEditData(prev => ({ ...prev, iva: e.target.value }))}
                                  />
                                  <Percent className="w-3 h-3 text-gray-400" />
                                </div>
                              ) : (
                                <span className="text-sm">{producto.iva}%</span>
                              )}
                            </td>
                            
                            <td className="px-4 py-3 text-center">
                              <span className="text-sm font-semibold text-green-600">
                                {editingProduct === producto.id 
                                  ? formatCurrency(calculateTotal(editData.unidad_basica, editData.precio_unitario, editData.iva))
                                  : formatCurrency(calculateTotal(producto.unidad_basica, producto.precio_unitario, producto.iva))
                                }
                              </span>
                            </td>
                            
                            {licitacion?.estado === 1 && (
                              <td className="px-4 py-3">
                                <div className="flex items-center justify-center gap-1">
                                  {editingProduct === producto.id ? (
                                    <>
                                      <button
                                        onClick={() => saveEditProduct(producto.id)}
                                        className="p-1 text-green-600 hover:bg-green-100 rounded"
                                        title="Guardar"
                                      >
                                        <Check className="w-4 h-4" />
                                      </button>
                                      <button
                                        onClick={cancelEdit}
                                        className="p-1 text-gray-600 hover:bg-gray-100 rounded"
                                        title="Cancelar"
                                      >
                                        <X className="w-4 h-4" />
                                      </button>
                                    </>
                                  ) : (
                                    <>
                                      {canEditProduct(producto) && (
                                        <button
                                          onClick={() => startEditProduct(producto)}
                                          className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                                          title="Editar"
                                        >
                                          <Edit className="w-4 h-4" />
                                        </button>
                                      )}
                                      {userPermissions.permisos === 1 && (
                                        <button
                                          onClick={() => handleDeleteProduct(producto.id)}
                                          className="p-1 text-red-600 hover:bg-red-100 rounded"
                                          title="Eliminar"
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </button>
                                      )}
                                    </>
                                  )}
                                </div>
                              </td>
                            )}
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal Nuevo Bloque - Solo admin */}
      {userPermissions.permisos === 1 && showNewBloqueModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Nuevo Bloque de Productos</h3>
                <button
                  onClick={() => setShowNewBloqueModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <form onSubmit={handleCreateBloque} className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nombre del Producto/Bloque *
                  </label>
                  <input
                    type="text"
                    required
                    className="w-full px-3 py-2 border-2 rounded-lg focus:outline-none focus:border-green-500"
                    value={newBloqueData.nombre}
                    onChange={(e) => setNewBloqueData(prev => ({ ...prev, nombre: e.target.value }))}
                    placeholder="Ej: Mascarillas quirúrgicas"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Se creará automáticamente una fila para cada proveedor invitado
                  </p>
                </div>
              </div>
              
              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowNewBloqueModal(false)}
                  className="flex-1 px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 px-4 py-2 text-white rounded-lg hover:opacity-90 disabled:opacity-50"
                  style={{backgroundColor: '#4DD5AF'}}
                >
                  {saving ? 'Creando...' : 'Crear Bloque'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Subir Excel - Solo admin */}
      {userPermissions.permisos === 1 && showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Cargar Excel</h3>
                <button
                  onClick={() => setShowUploadModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <form onSubmit={handleUploadExcel} className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Archivo Excel *
                  </label>
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    required
                    className="w-full px-3 py-2 border-2 rounded-lg focus:outline-none focus:border-green-500"
                    onChange={(e) => setUploadFile(e.target.files[0])}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    El Excel debe contener "DESCRIPCION ARTICULO" y columnas para cada proveedor
                  </p>
                </div>
              </div>
              
              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowUploadModal(false)}
                  className="flex-1 px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving || !uploadFile}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving ? 'Procesando...' : 'Cargar Excel'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Añadir Producto - Solo admin */}
      {userPermissions.permisos === 1 && showAddProductModal && selectedBloque && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Añadir Producto</h3>
                <button
                  onClick={() => setShowAddProductModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <form onSubmit={handleAddProduct} className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bloque: {selectedBloque.nombre}
                  </label>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tipo de Proveedor
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="tipo_proveedor"
                        value="existente"
                        checked={addProductData.tipo_proveedor === 'existente'}
                        onChange={(e) => setAddProductData(prev => ({ ...prev, tipo_proveedor: e.target.value }))}
                        className="mr-2"
                      />
                      Proveedor existente
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="tipo_proveedor"
                        value="nuevo"
                        checked={addProductData.tipo_proveedor === 'nuevo'}
                        onChange={(e) => setAddProductData(prev => ({ ...prev, tipo_proveedor: e.target.value }))}
                        className="mr-2"
                      />
                      Otro proveedor (nuevo)
                    </label>
                  </div>
                </div>
                
                {addProductData.tipo_proveedor === 'existente' ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Seleccionar Proveedor *
                    </label>
                    <select
                      required
                      className="w-full px-3 py-2 border-2 rounded-lg focus:outline-none focus:border-green-500"
                      value={addProductData.proveedor_id}
                      onChange={(e) => setAddProductData(prev => ({ ...prev, proveedor_id: e.target.value }))}
                    >
                      <option value="">-- Seleccione un proveedor --</option>
                      {proveedores.map(proveedor => (
                        <option key={proveedor.id} value={proveedor.id}>
                          {proveedor.nombre}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nombre del Proveedor *
                    </label>
                    <input
                      type="text"
                      required
                      className="w-full px-3 py-2 border-2 rounded-lg focus:outline-none focus:border-green-500"
                      value={addProductData.nuevo_proveedor}
                      onChange={(e) => setAddProductData(prev => ({ ...prev, nuevo_proveedor: e.target.value }))}
                      placeholder="Nombre del proveedor"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Este proveedor no será registrado en el sistema, solo se guardará su nombre
                    </p>
                  </div>
                )}
              </div>
              
              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowAddProductModal(false)}
                  className="flex-1 px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 px-4 py-2 text-white rounded-lg hover:opacity-90 disabled:opacity-50"
                  style={{backgroundColor: '#4DD5AF'}}
                >
                  {saving ? 'Añadiendo...' : 'Añadir Producto'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Spinner de carga para operaciones */}
      {(saving || downloading) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 shadow-xl">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 border-2 border-green-500 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-gray-700 font-medium">
                {downloading ? 'Descargando Excel...' : 'Procesando...'}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}